import React, { useState, useEffect } from 'react';
import { X, Copy, Save } from 'lucide-react';
import { useClonarDocumento, useTiposDocumentos } from '../../hooks/useDocumentos';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface CloneDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  documento: any; // Documento original a clonar
}

const CloneDocumentModal: React.FC<CloneDocumentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  documento,
}) => {
  const [formData, setFormData] = useState({
    tipo_documento: '',
    descripcion: '',
    fecha_vencimiento: '',
  });
  const [errors, setErrors] = useState<string[]>([]);

  const clonarMutation = useClonarDocumento();
  const { data: tiposDocumentoData, isLoading: loadingTipos } = useTiposDocumentos();

  const isLoading = clonarMutation.isLoading || loadingTipos;

  // Obtener tipos de documentos del response
  const tiposDocumento = (() => {
    if (!tiposDocumentoData) return [];
    if (Array.isArray(tiposDocumentoData)) return tiposDocumentoData;
    const data = (tiposDocumentoData as any)?.data;
    if (Array.isArray(data)) return data;
    return [];
  })();

  // Limpiar formulario al abrir
  useEffect(() => {
    if (isOpen) {
      setFormData({
        tipo_documento: '',
        descripcion: '',
        fecha_vencimiento: documento?.fecha_vencimiento ? documento.fecha_vencimiento.split('T')[0] : '',
      });
      setErrors([]);
    }
  }, [documento, isOpen]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar errores al cambiar input
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar datos básicos
    if (!formData.tipo_documento.trim()) {
      setErrors(['El tipo de documento es requerido.']);
      return;
    }

    try {
      // Preparar datos para clonación
      const cloneData: any = {
        tipo_documento: formData.tipo_documento.trim(),
      };

      if (formData.descripcion.trim()) {
        cloneData.descripcion = formData.descripcion.trim();
      }

      if (formData.fecha_vencimiento) {
        cloneData.fecha_vencimiento = formData.fecha_vencimiento;
      }

      // Clonar documento
      await clonarMutation.mutateAsync({
        id: documento.id,
        data: cloneData
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error al clonar documento:', error);
      
      if (error.response?.status === 400) {
        setErrors(['Los datos ingresados no son válidos. Por favor, verifique la información.']);
      } else if (error.response?.status === 404) {
        setErrors(['El documento original no fue encontrado.']);
      } else if (error.response?.status === 500) {
        setErrors(['Error del servidor. Por favor, intente nuevamente más tarde.']);
      } else {
        setErrors([`Error inesperado: ${error.message || 'Error desconocido'}`]);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto scrollbar-hide">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-start">
            <div className="flex items-center">
              <Copy className="h-6 w-6 mr-3" />
              <div>
                <h2 className="text-xl font-bold">Clonar Documento</h2>
                <p className="text-blue-100 text-sm">
                  {documento?.nombre_documento || 'Documento'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
              disabled={isLoading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Errores */}
          {errors.length > 0 && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="text-sm">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Información del documento original */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>Documento original:</strong> {documento?.nombre_documento}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              <strong>Tipo actual:</strong> {documento?.tipo_documento || 'N/A'}
            </p>
            <p className="text-xs text-blue-500 mt-1">
              El clon compartirá el mismo archivo físico pero con diferente tipo de documento.
            </p>
          </div>

          <div className="space-y-4">
            {/* Tipo de Documento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Tipo de Documento *
              </label>
              <select
                value={formData.tipo_documento}
                onChange={(e) => handleInputChange('tipo_documento', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
                required
              >
                <option value="">Seleccionar tipo de documento</option>
                {tiposDocumento.map((tipo: any) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                El tipo debe ser diferente al tipo del documento original.
              </p>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción (Opcional)
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) => handleInputChange('descripcion', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Descripción adicional para este clon..."
                rows={3}
                disabled={isLoading}
              />
            </div>

            {/* Fecha de Vencimiento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha de Vencimiento (Opcional)
              </label>
              <input
                type="date"
                value={formData.fecha_vencimiento}
                onChange={(e) => handleInputChange('fecha_vencimiento', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              />
            </div>

            {/* Información adicional */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs text-blue-600">
                <strong>💡 Clonación Lógica:</strong> El clon creará un nuevo registro en la base de datos 
                que apunta al mismo archivo en Google Drive. Esto permite tener múltiples tipos de documento 
                sin duplicar el archivo físico.
              </p>
            </div>
          </div>

          {/* Footer con botones */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner />
                  <span className="ml-2">Clonando...</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Crear Clon
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CloneDocumentModal;
